// ─────────────────────────────────────────────────────────────────────────────
// useSheetStack
//
// Coordinates z-ordering for nested/layered BottomSheet (and BottomSheet-composed
// dialogs like ConfirmSheet, SheetWizard, etc). Resolves MIDNIGHT redesign
// MASTER-PLAN conflict #4 (nested sheets) — first sheet gets zIndex 200, each
// nested sheet bumps by 10 (210, 220, ...).
//
//   const { depth, zIndex, push, pop } = useSheetStack()
//
// Each <BottomSheet> calls push() on mount and pop() on unmount. The returned
// depth/zIndex apply to that sheet instance. If no <SheetStackProvider> is
// mounted above, the hook falls back to a local depth=0 / zIndex=200 so callers
// still work — but then only one sheet at a time will z-stack correctly.
//
// Place a single <SheetStackProvider> near the root (e.g. around the app's
// top-level Router) to enable proper nested-sheet layering app-wide.
// ─────────────────────────────────────────────────────────────────────────────

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import React from 'react'
import { logger } from '../lib/logger'

/** Base z-index for the first (root-level) sheet. */
export const SHEET_Z_BASE = 200
/** Per-depth z-index increment. */
export const SHEET_Z_STEP = 10

interface SheetStackContextValue {
  /** Current number of stacked sheets. */
  depth: number
  /** Push a new sheet onto the stack; returns the depth assigned to that sheet. */
  push: () => number
  /** Pop the top sheet from the stack. */
  pop: () => void
}

const SheetStackContext = createContext<SheetStackContextValue | null>(null)

/**
 * Wrap your app (or any subtree that can host BottomSheets) with this
 * provider so nested sheets can coordinate z-index values.
 */
export function SheetStackProvider({ children }: { children: ReactNode }) {
  // We only need a mutable counter; re-rendering the whole tree on push/pop
  // would cause every open sheet to re-evaluate. The counter is read once at
  // the mount of each sheet (via push()), which is exactly when layout matters.
  const depthRef = useRef(0)
  const [, forceRender] = useState(0)

  const push = useCallback((): number => {
    // Depth cap: spec requires ≤ 2 stacked sheets (DESIGN-REVIEW-ACTIONS §2 criterion 5)
    if (depthRef.current >= 2) {
      if (import.meta.env.MODE !== 'production') {
        logger.warn(
          '[SheetStack] Sheet stack exceeds 2 levels — consider collapsing UX. ' +
          `Current depth: ${depthRef.current}`
        )
      }
    }
    const assigned = depthRef.current
    depthRef.current += 1
    // Nudge a render so any consumer reading `depth` directly stays accurate.
    forceRender((n) => (n + 1) % 1_000_000)
    return assigned
  }, [])

  const pop = useCallback(() => {
    depthRef.current = Math.max(0, depthRef.current - 1)
    forceRender((n) => (n + 1) % 1_000_000)
  }, [])

  const value = useMemo<SheetStackContextValue>(
    () => ({
      get depth() {
        return depthRef.current
      },
      push,
      pop,
    }),
    [push, pop],
  )

  return React.createElement(SheetStackContext.Provider, { value }, children)
}

export interface UseSheetStackReturn {
  /** Stack depth assigned to this hook instance (0 = root-level sheet). */
  depth: number
  /** Resolved z-index: `SHEET_Z_BASE + depth * SHEET_Z_STEP`. */
  zIndex: number
  /** Call on mount — returns the depth assigned to this sheet. */
  push: () => number
  /** Call on unmount. */
  pop: () => void
}

/**
 * Consumer hook for a single sheet. Reads the current depth from the nearest
 * `<SheetStackProvider>` (or falls back to depth 0 when no provider exists).
 *
 * Typical usage inside <BottomSheet>:
 *
 *   const { zIndex, push, pop } = useSheetStack()
 *   useEffect(() => { push(); return () => pop() }, [])
 *   // apply `zIndex` to the sheet + backdrop (backdrop uses `zIndex - 1`).
 */
export function useSheetStack(): UseSheetStackReturn {
  const ctx = useContext(SheetStackContext)

  // Each hook instance remembers the depth it was assigned at push() time. The
  // provider's shared counter handles the actual stack; we just snapshot.
  const assignedRef = useRef<number | null>(null)

  const push = useCallback((): number => {
    if (assignedRef.current !== null) return assignedRef.current
    if (ctx) {
      const d = ctx.push()
      assignedRef.current = d
      return d
    }
    assignedRef.current = 0
    return 0
  }, [ctx])

  const pop = useCallback(() => {
    if (assignedRef.current === null) return
    if (ctx) ctx.pop()
    assignedRef.current = null
  }, [ctx])

  const depth = assignedRef.current ?? (ctx ? ctx.depth : 0)
  const zIndex = SHEET_Z_BASE + depth * SHEET_Z_STEP

  return { depth, zIndex, push, pop }
}
